import { TARGET_APPS } from '@repo/config/Global.js';

/**
 * Display metadata for each target app — used by the auth app's app picker
 * and the in-app header app switcher.
 */
export const APP_META = Object.freeze({
    [TARGET_APPS.EDUCATION]: {
        icon: 'school',
        title: 'Education',
        description: 'Access your courses, classrooms, and learning materials',
    },
    [TARGET_APPS.TAP]: {
        icon: 'library_books',
        title: 'TA',
        description: 'Teacher Access Only',
    },
    [TARGET_APPS.ADMIN]: {
        icon: 'admin_panel_settings',
        title: 'Admin',
        description: 'Manage tenants, providers, and platform settings',
    },
    [TARGET_APPS.PERSONAL]: {
        icon: 'auto_stories',
        title: 'Personal',
        description: 'Access the BIOZONE titles you have purchased',
    },
});

/**
 * Resolve the list of apps the current user can access. Prefers the `apps`
 * custom claim on the ID token; falls back to the `getUserApps` Cloud Function
 * which checks each app's Firestore DB, writes the claim server-side, then
 * forces a token refresh so future reads get the fresh claim.
 *
 * @param {import('firebase/auth').User | null | undefined} user
 * @param {(name: string) => (...args) => Promise} callable - from createFunctions
 * @returns {Promise<string[]>} e.g. ['education', 'tap']
 */
export async function getUserApps(user, callable) {
    if (!user) return [];

    const tokenResult = await user.getIdTokenResult();
    const claimed = tokenResult?.claims?.apps;
    if (Array.isArray(claimed)) return claimed;

    const getUserAppsFn = callable('getUserApps');
    const { data } = await getUserAppsFn();
    await user.getIdToken(true);
    return Array.isArray(data?.apps) ? data.apps : [];
}

/**
 * Read a target app's origin out of an app-local URL map. Each app keeps its
 * own map because Vite only substitutes the `import.meta.env.VITE_*` literals
 * it sees in the consuming app's own source.
 *
 * @param {Record<string, string | (() => string | undefined)>} appUrls
 * @param {string} targetApp
 * @returns {string | null}
 */
export function resolveAppUrl(appUrls, targetApp) {
    const entry = appUrls?.[targetApp];
    const url = typeof entry === 'function' ? entry() : entry;
    return url || null;
}

/**
 * Shape raw app ids into switcher entries, dropping any target we have no URL
 * for — an unconfigured or undeployed sibling should leave no menu entry
 * rather than one that throws on click. `currentApp` survives the filter
 * regardless: the switcher renders it as "you're here" and never navigates.
 *
 * @param {string[]} appIds
 * @param {object} opts
 * @param {Record<string, string | (() => string | undefined)>} opts.appUrls
 * @param {string | null} [opts.currentApp]
 * @returns {Array<{ id: string, icon: string, title: string, description: string }>}
 */
export function buildAppSwitchTargets(
    appIds,
    { appUrls, currentApp = null } = {}
) {
    if (!Array.isArray(appIds)) return [];

    return [...new Set(appIds)]
        .filter((id) => APP_META[id])
        .filter((id) => id === currentApp || resolveAppUrl(appUrls, id))
        .map((id) => ({ id, ...APP_META[id] }));
}

/**
 * Load the switch targets once the session actually has a user, and again if
 * the user changes. Mount timing is not a safe trigger: on a `?token=` handoff
 * Firebase resolves its initial persistence check before
 * signInWithCustomToken settles, so `auth.currentUser` is still null when a
 * root layout mounts. The app's own user store is the honest signal.
 *
 * @param {{ subscribe: (run: (value: any) => void) => (() => void) }} userStore
 * @param {object} opts
 * @param {() => Promise<Array>} opts.load - resolves targets for the signed-in user
 * @param {(targets: Array) => void} opts.onTargets
 * @param {(err: unknown) => void} [opts.onError]
 * @returns {() => void} unsubscribe
 */
export function watchAppSwitchTargets(userStore, { load, onTargets, onError }) {
    let loadedUid = null;

    return userStore.subscribe((user) => {
        const uid = user?.uid ?? null;
        if (uid === loadedUid) return;
        loadedUid = uid;

        if (!uid) {
            onTargets([]);
            return;
        }

        load()
            .then((targets) => {
                if (loadedUid !== uid) return;
                onTargets(targets);
            })
            .catch((err) => {
                if (loadedUid !== uid) return;
                // Clear the marker so a later emission for this user retries.
                loadedUid = null;
                onTargets([]);
                onError?.(err);
            });
    });
}

/**
 * Mint a custom token for the target app and redirect there. The receiving
 * app consumes `?token=<token>` via handleAuthTokenHandoff.
 *
 * @param {object} opts
 * @param {(name: string) => (...args) => Promise} opts.callable
 * @param {'education' | 'tap' | 'admin'} opts.targetApp
 * @param {string} opts.appUrl - destination origin (e.g. VITE_TAP_URL)
 * @returns {Promise<void>}
 */
export async function switchToApp({ callable, targetApp, appUrl }) {
    if (!appUrl) throw new Error(`Missing URL for target app: ${targetApp}`);

    const generateAppTokenFn = callable('generateAppToken');
    const { data } = await generateAppTokenFn({ targetApp });
    const token = data?.token;
    if (!token) throw new Error('generateAppToken returned no token');

    if (typeof window !== 'undefined') {
        window.location.href = `${appUrl}?token=${token}`;
    }
}
